import React, { useEffect, useState } from 'react';
import { Box, FormHelperText, FormLabel, Grid, MenuItem, Select, styled, useTheme } from '@mui/material';
import {
  InfoRounded,
  Language as LanguageIcon,
  MailOutline as MailOutlineIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import SbTextField from '../../../components/Textfield';
import Contact from '../../../components/Contact';
import GoogleMaps from '../../../components/Location';
import SbButton from '../../../components/Button';
import ColorPicker from '../../../components/ColorPicker';
import SbTooltip from '../../../components/Tooltip';
import { FormikErrors, FormikHandlers, FormikHelpers, FormikValues } from 'formik';
import { OrganizationType, TaxInformationType } from './type';
import DragDrop, { DragDropProps } from '../../../components/File/DragDrop';
import { GoogleMapsTypes } from '../../../components/Location/Type';
import { RootStateType } from '@Store';
import { useSelector } from 'react-redux';
import Image from '@Components/Image';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { isActionAllowed } from '../../../utils/allowedActions';
import { ACTION_NAMES, PERFORM_ACTION } from '@Constants/actionsNames';
import { lighten, darken } from 'polished';
import { useAiValidation } from '../../../hooks/useAiValidation';

interface FormProps {
  values: FormikValues;
  handleChange: FormikHandlers['handleChange'];
  setFieldValue?: FormikHelpers<OrganizationType>['setFieldValue'];
  errors?: FormikErrors<OrganizationType>;
  handleSelectedAddress: GoogleMapsTypes['onSelect'];
  onFileChange: DragDropProps['onChange'];
}

export const ContainerStyle = styled(Grid)(({ theme }) => ({
  padding: '20px 0',
  borderTop: `1px solid ${theme.palette.border.grey}`,
}));

interface ColorBoxProps {
  label: string;
  color: string;
  tooltip?: string;
}

const ColorBox: React.FC<ColorBoxProps> = ({ label, color, tooltip }) => (
  <div style={{ margin: '1rem 5px', textAlign: 'center' }} title={tooltip}>
    <div
      style={{
        width: '25px',
        height: '25px',
        backgroundColor: color,
        margin: '0 auto',
        borderRadius: '8px',
        border: '1px solid #ccc',
      }}
    ></div>
    <p>{label}</p>
    <p style={{ fontSize: '0.8rem' }}>{color}</p>
  </div>
);

const THEME_PRESETS = [
  {
    id: 'professional-blue',
    name: 'Professional Blue',
    primaryColor: '#1E3A8A',
    secondaryColor: '#3B82F6',
  },
  {
    id: 'modern-indigo',
    name: 'Modern Indigo',
    primaryColor: '#4338CA',
    secondaryColor: '#6366F1',
  },
  {
    id: 'teal',
    name: 'Teal',
    primaryColor: '#0F766E',
    secondaryColor: '#14B8A6',
  },
  {
    id: 'elegant-navy',
    name: 'Elegant Navy',
    primaryColor: '#1E293B',
    secondaryColor: '#475569',
  },
  {
    id: 'slate-orange',
    name: 'Slate + Orange',
    primaryColor: '#334155',
    secondaryColor: '#F97316',
  },
  {
    id: 'emerald-sage',
    name: 'Emerald Sage',
    primaryColor: '#1B4332',
    secondaryColor: '#52B788',
  },
  {
    id: 'charcoal-amber',
    name: 'Charcoal Amber',
    primaryColor: '#27272A',
    secondaryColor: '#F59E0B',
  },
  {
    id: 'royal-purple',
    name: 'Royal Purple',
    primaryColor: '#581C87',
    secondaryColor: '#A855F7',
  },
  {
    id: 'crimson-slate',
    name: 'Crimson Slate',
    primaryColor: '#991B1B',
    secondaryColor: '#64748B',
  },
  {
    id: 'ocean-sky',
    name: 'Ocean Sky',
    primaryColor: '#0369A1',
    secondaryColor: '#0EA5E9',
  },
  {
    id: 'deep-forest',
    name: 'Deep Forest',
    primaryColor: '#052E16',
    secondaryColor: '#22C55E',
  },
  {
    id: 'amethyst-violet',
    name: 'Amethyst Violet',
    primaryColor: '#3B0764',
    secondaryColor: '#D8B4FE',
  },
  {
    id: 'burgundy-blush',
    name: 'Burgundy Blush',
    primaryColor: '#4C0519',
    secondaryColor: '#E8C5C8',
  },
  {
    id: 'deep-cyan',
    name: 'Deep Cyan',
    primaryColor: '#164E63',
    secondaryColor: '#06B6D4',
  },
  {
    id: 'charcoal-gold',
    name: 'Charcoal Gold',
    primaryColor: '#09090B',
    secondaryColor: '#D4AF37',
  },
  {
    id: 'forest-slate',
    name: 'Forest Slate',
    primaryColor: '#0F291B',
    secondaryColor: '#94A3B8',
  },
  {
    id: 'plum-rose',
    name: 'Plum Rose',
    primaryColor: '#701A75',
    secondaryColor: '#F472B6',
  },
  {
    id: 'steel-graphite',
    name: 'Steel Graphite',
    primaryColor: '#3B4D61',
    secondaryColor: '#6C7A89',
  },
  {
    id: 'copper-coffee',
    name: 'Copper Coffee',
    primaryColor: '#451A03',
    secondaryColor: '#B45309',
  },
  {
    id: 'midnight-cyan',
    name: 'Midnight Cyan',
    primaryColor: '#0F172A',
    secondaryColor: '#06B6D4',
  },
  {
    id: 'crimson-professional',
    name: 'Crimson Professional',
    primaryColor: '#881337',
    secondaryColor: '#1E293B',
  },
  {
    id: 'ruby',
    name: 'Ruby',
    primaryColor: '#BE123C',
    secondaryColor: '#4C0519',
  },
  {
    id: 'scarlet',
    name: 'Scarlet',
    primaryColor: '#DC2626',
    secondaryColor: '#334155',
  },
  {
    id: 'rosewood',
    name: 'Rosewood',
    primaryColor: '#9E4759',
    secondaryColor: '#3B0713',
  },
  {
    id: 'red-navy',
    name: 'Red & Navy',
    primaryColor: '#B91C1C',
    secondaryColor: '#0F172A',
  },
];

const normalizeColor = (color?: string) => color?.trim().toLowerCase() || '';

const getMatchingThemePreset = (primaryColor?: string, secondaryColor?: string) => {
  const normPrimary = normalizeColor(primaryColor);
  const normSecondary = normalizeColor(secondaryColor);
  return (
    THEME_PRESETS.find(
      preset =>
        normalizeColor(preset.primaryColor) === normPrimary && normalizeColor(preset.secondaryColor) === normSecondary,
    ) || null
  );
};

function OrganizationSetting(props: FormProps) {
  const { values, errors, handleChange, setFieldValue, handleSelectedAddress, onFileChange } = props;
  const [taxName, setTaxName] = useState<string>('');
  const [taxValue, setTaxValue] = useState<string>('');
  const theme = useTheme();
  const [primaryColor, setPrimaryColor] = useState<string>(values.theme.primary.main || theme.palette.primary.main);
  const [secondaryColor, setSecondaryColor] = useState<string>(
    values.theme.secondary.main || theme.palette.secondary.main,
  );

  useEffect(() => {
    const val = values.theme?.primary?.main || theme.palette.primary.main;
    if (val && normalizeColor(val) !== normalizeColor(primaryColor)) {
      setPrimaryColor(val);
    }
  }, [values.theme?.primary?.main, theme.palette.primary.main]);

  useEffect(() => {
    const val = values.theme?.secondary?.main || theme.palette.secondary.main;
    if (val && normalizeColor(val) !== normalizeColor(secondaryColor)) {
      setSecondaryColor(val);
    }
  }, [values.theme?.secondary?.main, theme.palette.secondary.main]);

  const matchedPreset = getMatchingThemePreset(primaryColor, secondaryColor);
  const selectedThemeId = matchedPreset ? matchedPreset.id : null;
  const { countriesObj, countries } = useSelector((state: RootStateType) => state.global);
  const organizationData = useSelector((state: RootStateType) => state.orgSetup.organizationPublicData);
  const { name = '' } = organizationData || {};
  const { menuAcccessGroupById: accessGroupById } = useSelector((state: RootStateType) => state.accessGroup);

  const { validationStates, validate } = useAiValidation();

  // Org Email
  useEffect(() => {
    if (values.contactInfo?.email) {
      validate('email', 'email', values.contactInfo.email);
    }
  }, [values.contactInfo?.email, validate]);

  // Support Email
  useEffect(() => {
    if (values.contactInfo?.supportEmail) {
      validate('supportEmail', 'email', values.contactInfo.supportEmail);
    }
  }, [values.contactInfo?.supportEmail, validate]);

  // Contact number
  useEffect(() => {
    if (values.contactInfo?.contactNumber) {
      validate('contactNumber', 'phone', values.contactInfo.contactNumber);
    }
  }, [values.contactInfo?.contactNumber, validate]);

  // Address (Pincode & Country)
  useEffect(() => {
    const address = values.contactInfo?.address;
    if (address?.country && address?.pincode) {
      const countryDisplay = countriesObj?.[address.country]?.name || address.country;
      validate('address', 'address', {
        country: countryDisplay,
        pincode: address.pincode,
      });
    }
  }, [values.contactInfo?.address?.country, values.contactInfo?.address?.pincode, countriesObj, validate]);

  // Tax Info (GST / PAN)
  useEffect(() => {
    if (taxName && taxValue) {
      const lowerName = taxName.toLowerCase();
      if (lowerName.includes('gst')) {
        validate('taxValue', 'gst', taxValue);
      } else if (lowerName.includes('pan')) {
        validate('taxValue', 'pan', taxValue);
      }
    }
  }, [taxName, taxValue, validate]);

  const handleApplyEmailSuggestion = (suggestion: string) => {
    setFieldValue && setFieldValue('contactInfo.email', suggestion);
  };

  const handleApplySupportEmailSuggestion = (suggestion: string) => {
    setFieldValue && setFieldValue('contactInfo.supportEmail', suggestion);
  };

  const handleApplyContactSuggestion = (suggestion: string) => {
    setFieldValue && setFieldValue('contactInfo.contactNumber', suggestion);
  };

  const handleApplyPincodeSuggestion = (suggestion: string) => {
    setFieldValue && setFieldValue('contactInfo.address.pincode', suggestion);
  };

  const handleApplyTaxSuggestion = (suggestion: string) => {
    setTaxValue(suggestion);
  };

  const isGeneralWriteAllowed = isActionAllowed(
    accessGroupById?.actions,
    ACTION_NAMES.Organization_Settings.General,
    PERFORM_ACTION.write,
  );

  const addTaxInfo = () => {
    if (!setFieldValue) return;

    setFieldValue('taxInformation', [...values.taxInformation, { name: taxName, value: taxValue }]);
    setTaxName('');
    setTaxValue('');
  };
  const countryName = countriesObj?.[values.contactInfo.address.country]?.name || '';

  useEffect(() => {
    const theme = {
      primary: {
        main: primaryColor,
        dark: darken(0.2, primaryColor),
        light: name === 'Cargo Solutions Network' ? lighten(0.8, primaryColor) : lighten(0.5, primaryColor),
        contrastText: '#fff',
      },
      secondary: {
        main: secondaryColor,
        light: lighten(0.5, secondaryColor),
      },
    };
    if (setFieldValue) {
      setFieldValue('theme', theme);
    } else {
      console.warn('setFieldValue is undefined');
    }
  }, [primaryColor, secondaryColor]);

  return (
    <div>
      {/* ORG NAME----------------------------------------------------------------------------------------------------------------- */}
      <ContainerStyle container alignItems="center">
        <Grid item xs={12} lg={4} xl={3} mb={{ xs: 1 }}>
          <SBLabel
            label="Organization Name"
            required
            tooltipText="Your organization name visible to your customers & staff while using dashboard"
          />
        </Grid>
        <Grid container item xs={12} md={6} lg={4}>
          <Grid item xs={12}>
            <SbTextField
              name="name"
              placeholder="Enter Organization Name"
              value={values.name}
              onChange={handleChange}
              error={errors?.name || ''}
              disabled={!isGeneralWriteAllowed}
            />
          </Grid>
        </Grid>
      </ContainerStyle>
      {/* EMAIL----------------------------------------------------------------------------------------------------------------- */}
      <ContainerStyle container alignItems="center">
        <Grid item xs={12} lg={4} xl={3} mb={{ xs: 1 }}>
          <SBLabel
            label="Organization Email"
            required
            tooltipText="This email will be visible on your dashboard & Log in Page"
          />
        </Grid>
        <Grid container item xs={12} md={6} lg={4}>
          <Grid item xs={12}>
            <SbTextField
              placeholder="Enter Organization Email"
              value={values.contactInfo.email}
              name={'contactInfo.email'}
              startAdornment={<MailOutlineIcon sx={{ mr: 1 }} />}
              error={errors?.contactInfo?.email || ''}
              onChange={handleChange('contactInfo.email')}
              disabled={!isGeneralWriteAllowed}
              validationState={validationStates['email']}
              onApplySuggestion={handleApplyEmailSuggestion}
            />
          </Grid>
        </Grid>
      </ContainerStyle>
      {/* SUPPORT EMAIL----------------------------------------------------------------------------------------------------------------- */}

      <ContainerStyle container alignItems="center">
        <Grid item xs={12} lg={4} xl={3} mb={{ xs: 1 }}>
          <SBLabel
            label="Organization Support Email"
            required
            tooltipText="This email will be used to send user onboarding invitation"
          />
        </Grid>
        <Grid container item xs={12} md={6} lg={4}>
          <Grid item xs={12}>
            <SbTextField
              placeholder="Enter Organization Support Email"
              name="contactInfo.supportEmail"
              value={values.contactInfo.supportEmail}
              startAdornment={<MailOutlineIcon sx={{ mr: 1 }} />}
              error={errors?.contactInfo?.supportEmail || ''}
              onChange={handleChange('contactInfo.supportEmail')}
              disabled={!isGeneralWriteAllowed}
              validationState={validationStates['supportEmail']}
              onApplySuggestion={handleApplySupportEmailSuggestion}
            />
          </Grid>
        </Grid>
      </ContainerStyle>
      {/* CONTACT----------------------------------------------------------------------------------------------------------------- */}

      <ContainerStyle container alignItems="center">
        <Grid item xs={12} lg={4} xl={3} mb={{ xs: 1 }}>
          <SBLabel
            label="Organization Contact"
            required
            tooltipText="This email will be used to send user onboarding invitation"
          />
        </Grid>
        <Grid container item xs={12} md={6} lg={4}>
          <Grid item xs={12}>
            <Contact
              phoneName="contact_number"
              phoneNumber={values.contactInfo.contactNumber}
              onChangePhoneNumber={handleChange('contactInfo.contactNumber')}
              telCountryCodeName="contactInfo.contactNumber"
              telCountryCode={values.contactInfo.telephoneCode}
              onChangeTelCountryCode={event => {
                if (!setFieldValue) return;
                setFieldValue('contactInfo.telephoneCode', event.target.value);
              }}
              disabled={!isGeneralWriteAllowed}
              validationState={validationStates['contactNumber']}
              onApplySuggestion={handleApplyContactSuggestion}
            />
          </Grid>
        </Grid>
      </ContainerStyle>
      {/* ADDRESS----------------------------------------------------------------------------------------------------------------- */}

      <ContainerStyle container alignItems="start">
        <Grid item xs={12} lg={4} xl={3} mb={{ xs: 1 }}>
          <SBLabel
            label="Organization address"
            tooltipText="This address will be visible on your dashboard & Log in Page"
          />
        </Grid>
        <Grid container item xs={12} lg={8} xl={9} spacing={3}>
          <Grid item xs={12}>
            <GoogleMaps
              onSelect={handleSelectedAddress}
              label={'Location'}
              placeholder="Enter location"
              // reinitialize={reinitialize}
              disabled={!isGeneralWriteAllowed}
            />
          </Grid>
          <Grid container item xs={12} md={6} lg={6}>
            <SbTextField
              className="w-full"
              label="Address Line 1"
              placeholder="Enter Address Line 1"
              value={values.contactInfo.address.line1}
              onChange={handleChange('contactInfo.address.line1')}
              name="contactInfo.address.line1"
              error={errors?.contactInfo?.address?.line1 || ''}
              disabled={!isGeneralWriteAllowed}
            />
          </Grid>
          <Grid container item xs={12} md={6} lg={6}>
            <SbTextField
              className="w-full"
              label="Address Line 2"
              placeholder="Enter Address Line 2"
              value={values.contactInfo.address.line2}
              onChange={handleChange('contactInfo.address.line2')}
              name="contactInfo.address.line2"
              error={errors?.contactInfo?.address?.line2 || ''}
              disabled={!isGeneralWriteAllowed}
            />
          </Grid>
          <Grid container item xs={12} md={6} lg={6}>
            <SbTextField
              className="w-full"
              label="City"
              placeholder="Enter City"
              value={values.contactInfo.address.city}
              onChange={handleChange('contactInfo.address.city')}
              name="contactInfo.address.city"
              error={errors?.contactInfo?.address?.city || ''}
              disabled={!isGeneralWriteAllowed}
            />
          </Grid>
          <Grid container item xs={12} md={6} lg={6}>
            <SbTextField
              className="w-full"
              label="State"
              placeholder="Enter State"
              value={values.contactInfo.address.state}
              onChange={handleChange('contactInfo.address.state')}
              name="contactInfo.address.state"
              error={errors?.contactInfo?.address?.state || ''}
              disabled={!isGeneralWriteAllowed}
            />
          </Grid>
          <Grid container item xs={12} md={6} lg={6}>
            <SbTextField
              required
              disabled
              className="w-full"
              label="Country"
              placeholder="Enter Country"
              name="contactInfo.address.country"
              value={countryName}
            />
          </Grid>
          <Grid container item xs={12} md={6} lg={6}>
            <SbTextField
              className="w-full"
              label="Pincode"
              placeholder="Enter Pincode"
              value={values.contactInfo.address.pincode}
              onChange={handleChange('contactInfo.address.pincode')}
              name="contactInfo.address.pincode"
              error={errors?.contactInfo?.address?.pincode || ''}
              disabled={!isGeneralWriteAllowed}
              validationState={validationStates['address']}
              onApplySuggestion={handleApplyPincodeSuggestion}
            />
          </Grid>
        </Grid>
      </ContainerStyle>
      {/* TAX INFO----------------------------------------------------------------------------------------------------------------- */}

      <ContainerStyle container alignItems="start">
        <Grid item xs={12} lg={4} xl={3} mb={{ xs: 1 }}>
          <SBLabel label="Tax Information" tooltipText="Add your company tax information like GSTIN & PAN" />
        </Grid>
        <Grid container item xs={12} lg={8} xl={9} spacing={3}>
          <Grid container item alignItems="center" spacing={3}>
            {values.taxInformation.map((taxInfo: TaxInformationType, index: number) => (
              <React.Fragment key={`org-company-tax-info-${index}`}>
                <Grid item xs={6} sm={6} md={6} lg={5}>
                  {taxInfo.name}
                </Grid>

                <Grid item xs={6} sm={6} md={6} lg={5}>
                  {taxInfo.value}
                </Grid>
              </React.Fragment>
            ))}
          </Grid>
          <Grid container item alignItems="flex-end" spacing={3}>
            <Grid container item xs={6} sm={6} md={6} lg={5}>
              <Grid item xs={12}>
                <SbTextField
                  placeholder="Tax Name"
                  // label="Tax name"
                  value={taxName}
                  onChange={event => setTaxName(event.target.value)}
                  name="taxName"
                  // error={errors.line1}
                  disabled={!isGeneralWriteAllowed}
                />
              </Grid>
            </Grid>
            <Grid container item xs={6} sm={6} md={6} lg={5}>
              <Grid item xs={12}>
                <SbTextField
                  placeholder="Tax Number"
                  // label="Tax number"
                  value={taxValue}
                  onChange={event => setTaxValue(event.target.value)}
                  name="taxNumber"
                  // error={errors.line1}
                  disabled={!isGeneralWriteAllowed}
                  validationState={validationStates['taxValue']}
                  onApplySuggestion={handleApplyTaxSuggestion}
                />
              </Grid>
            </Grid>
            <Grid
              container
              item
              xs={5}
              sm={3}
              md={2}
              alignItems="end"
              justifyContent={{ xs: 'flex-end', lg: 'flex-end' }}
            >
              <SbButton
                disabled={taxName.trim() && taxValue.trim() ? false : true}
                variant="contained"
                className="w-full"
                onClick={addTaxInfo}
              >
                Add
              </SbButton>
            </Grid>
          </Grid>
        </Grid>
      </ContainerStyle>
      {/* ----------------------------------------------------------------------------------------------------------------- */}

      {/* <ContainerStyle container alignItems="center">
        <Grid item xs={12} lg={4} xl={3}>
          <SBLabel label="First User" tooltipText="The first user - Admin on the Speedbox Dashboard" />
        </Grid>
        <Grid container item xs={12} md={6} lg={4} alignItems="center" justifyContent={{ xs: 'space-between' }}>
          <Grid item xs={12} md={11}>
            <SbTextField
              name="firstUserEmail"
              value={values.firstUserEmail}
              startAdornment={<MailOutlineIcon sx={{ mr: 1 }} />}
              disabled
              error={errors?.firstUserEmail || ''}
              //   onChange={handleChange}
            />
          </Grid>
        </Grid>
      </ContainerStyle> */}
      {/* ----------------------------------------------------------------------------------------------------------------- */}

      <ContainerStyle container alignItems="center">
        <Grid item xs={12} lg={4} xl={3}>
          <SBLabel label="Website Link" tooltipText="Website link of the organization if any" />
        </Grid>
        <Grid container item xs={12} md={6} lg={4} alignItems="center" justifyContent={{ xs: 'space-between' }}>
          <Grid item xs={12} md={11}>
            <SbTextField
              name="websiteLink"
              placeholder="Enter Website Link"
              value={values.websiteLink}
              startAdornment={<LanguageIcon sx={{ mr: 1 }} />}
              onChange={handleChange}
              error={errors?.websiteLink || ''}
              disabled={!isGeneralWriteAllowed}
            />
          </Grid>
        </Grid>
      </ContainerStyle>
      <ContainerStyle container alignItems="center">
        <Grid item xs={12} lg={4} xl={3}>
          <SBLabel label="Select Currency" tooltipText="Select currency for the organization" />
        </Grid>
        <Grid container item xs={12} md={6} lg={4} alignItems="center" justifyContent={{ xs: 'space-between' }}>
          <Grid item xs={12} md={11}>
            <Select
              sx={{ width: '100%' }}
              name="currency"
              value={values.currency || ''}
              displayEmpty
              onChange={handleChange}
            >
              <MenuItem value="" disabled>
                Select a currency
              </MenuItem>
              {countries.map((item, ind) => (
                <MenuItem key={ind} value={item.currency.name}>
                  {item.currency.name} ({item.currency.symbol})
                </MenuItem>
              ))}
            </Select>
          </Grid>
        </Grid>
      </ContainerStyle>
      {/* ----------------------------------------------------------------------------------------------------------------- */}

      <ContainerStyle container alignItems="start">
        <Grid item xs={12} lg={4} xl={3}>
          <SBLabel label="Logo" />
        </Grid>
        <Grid container item xs={12} lg={8} xl={9} alignItems="center" justifyContent={{ xs: 'space-between' }}>
          <Grid item xs={12} md={11}>
            {values.logo ? (
              <Grid container position="relative">
                <Image src={values.logo} alt={values.name} width={100} style={{ maxHeight: '200px' }} />
                {isGeneralWriteAllowed && (
                  <span
                    // className="absolute cursor-pointer"
                    style={{ top: 2 }}
                    onClick={() => {
                      props.setFieldValue && props.setFieldValue('logo', '');
                    }}
                  >
                    <DeleteOutlineIcon />
                  </span>
                )}
              </Grid>
            ) : (
              <DragDrop
                accept="image/*"
                onChange={onFileChange}
                subText={
                  values.logoFile && typeof values.logoFile === 'object'
                    ? `${values.logoFile.fileName} (${values.logoFile.fileSize})`
                    : 'Supported formats: JPEG, PNG (450x225px, File upto 5 mb)'
                }
                // subText="Supported formats: JPEG, PNG (450x225px, File upto 5 mb)"
                readType="base64"
              />
            )}
            {errors?.logoFile && (
              <FormHelperText error className="m-0">
                {errors.logoFile}
              </FormHelperText>
            )}
          </Grid>
        </Grid>
      </ContainerStyle>
      {/* ----------------------------------------------------------------------------------------------------------------- */}

      <ContainerStyle container alignItems="start">
        <Grid item xs={12} lg={4} xl={3}>
          <SBLabel
            label="Theme Customization"
            tooltipText="Select a predefined theme or fine-tune custom colors for your organization's brand"
          />
        </Grid>
        <Grid container item xs={12} lg={8} xl={9} alignItems="start" justifyContent={{ xs: 'space-between' }}>
          <Grid item xs={12} md={11}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Presets Header */}
              <Box sx={{ mb: 2 }}>
                <span style={{ fontWeight: 600, fontSize: '1.1rem', color: theme.palette.text.primary || '#080808' }}>
                  Choose a predefined theme
                </span>
                <p style={{ margin: '4px 0 0 0', fontSize: '0.85rem', color: '#64748b' }}>
                  Select a professionally designed color palette or customize your colors manually.
                </p>
              </Box>

              {/* Responsive Grid of Cards */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, 1fr)',
                    md: 'repeat(3, 1fr)',
                    lg: 'repeat(5, 1fr)',
                  },
                  gap: 2,
                  mb: 3,
                }}
              >
                {THEME_PRESETS.map(preset => {
                  const isSelected = selectedThemeId === preset.id;
                  return (
                    <Box
                      key={preset.id}
                      component="button"
                      type="button"
                      disabled={!isGeneralWriteAllowed}
                      onClick={() => {
                        if (isGeneralWriteAllowed) {
                          setPrimaryColor(preset.primaryColor);
                          setSecondaryColor(preset.secondaryColor);
                        }
                      }}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        padding: '12px',
                        borderRadius: '8px',
                        border: isSelected
                          ? `2px solid ${preset.primaryColor}`
                          : `1px solid ${theme.palette.border?.grey || '#e0e0e0'}`,
                        backgroundColor: isSelected ? '#f8fafc' : '#ffffff',
                        boxShadow: isSelected ? '0px 4px 12px rgba(0, 0, 0, 0.08)' : '0px 1px 2px rgba(0, 0, 0, 0.02)',
                        cursor: isGeneralWriteAllowed ? 'pointer' : 'not-allowed',
                        opacity: isGeneralWriteAllowed ? 1 : 0.7,
                        textAlign: 'left',
                        outline: 'none',
                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                        width: '100%',
                        transform: 'translateY(0)',
                        '&:hover': isGeneralWriteAllowed
                          ? {
                              borderColor: preset.primaryColor,
                              boxShadow: '0px 6px 16px rgba(0, 0, 0, 0.06)',
                              transform: 'translateY(-2px)',
                            }
                          : {},
                        '&:focus-visible': {
                          outline: `2px solid ${preset.primaryColor}`,
                          outlineOffset: '2px',
                        },
                      }}
                    >
                      {/* Split color preview bar */}
                      <Box
                        sx={{
                          display: 'flex',
                          height: '24px',
                          width: '100%',
                          borderRadius: '4px',
                          overflow: 'hidden',
                          mb: 1.5,
                        }}
                      >
                        <Box sx={{ flex: 1, backgroundColor: preset.primaryColor }} />
                        <Box sx={{ flex: 1, backgroundColor: preset.secondaryColor }} />
                      </Box>

                      {/* Theme info container */}
                      <Box
                        sx={{
                          display: 'flex',
                          width: '100%',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                        }}
                      >
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                          <span style={{ fontWeight: isSelected ? 600 : 500, fontSize: '0.85rem', color: '#1e293b' }}>
                            {preset.name}
                          </span>
                          <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                            {preset.primaryColor} • {preset.secondaryColor}
                          </span>
                        </Box>
                        {isSelected && (
                          <CheckCircleIcon sx={{ color: preset.primaryColor, fontSize: '1.2rem', mt: 0.25 }} />
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Box>

              {/* Dynamic Active Theme Summary Panel */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  padding: '16px',
                  borderRadius: '8px',
                  backgroundColor: matchedPreset ? `${matchedPreset.primaryColor}0a` : '#f8fafc',
                  border: `1px dashed ${matchedPreset ? matchedPreset.primaryColor : '#cbd5e1'}`,
                  mb: 3,
                  transition: 'all 0.3s ease',
                }}
              >
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: matchedPreset ? matchedPreset.primaryColor : '#475569',
                    color: '#fff',
                    transition: 'all 0.3s ease',
                  }}
                >
                  {matchedPreset ? <CheckCircleIcon sx={{ fontSize: '1.2rem' }} /> : 'C'}
                </Box>
                <Box>
                  <span style={{ display: 'block', fontWeight: 600, fontSize: '0.9rem', color: '#1e293b' }}>
                    {matchedPreset ? `✓ ${matchedPreset.name}` : 'Custom Theme'}
                  </span>
                  <span style={{ fontSize: '0.8rem', color: '#64748b' }}>
                    {matchedPreset
                      ? `Primary: ${matchedPreset.primaryColor} • Secondary: ${matchedPreset.secondaryColor}`
                      : 'Using custom brand colors'}
                  </span>
                </Box>
              </Box>

              {/* Divider */}
              <Box sx={{ borderTop: `1px solid ${theme.palette.divider || '#e0e0e0'}`, my: 3 }} />

              {/* Custom Colors Title */}
              <Box sx={{ mb: 2 }}>
                <span style={{ fontWeight: 600, fontSize: '1.1rem', color: theme.palette.text.primary || '#080808' }}>
                  Custom Colors
                </span>
                <p style={{ margin: '4px 0 0 0', fontSize: '0.85rem', color: '#64748b' }}>
                  Fine-tune your organization's brand colors.
                </p>
              </Box>

              {/* Custom Color Pickers */}
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4 }}>
                {/* First Color Picker Box */}
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ mb: 1, display: 'flex', flexDirection: 'column' }}>
                    <span style={{ fontSize: '0.9rem', fontWeight: 600, color: '#334155' }}>Primary Color</span>
                    <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                      Used for main headers, buttons, and active elements.
                    </span>
                  </Box>
                  <ColorPicker
                    setValue={setPrimaryColor}
                    value={primaryColor}
                    disabled={!isGeneralWriteAllowed}
                    sx={{ width: '100%', mb: 1.5 }}
                  />
                  <Box sx={{ display: 'flex', gap: '5px' }}>
                    <ColorBox
                      label="Primary - Light"
                      color={values.theme.primary?.light}
                      tooltip="This color will be visible on hover"
                    />
                  </Box>
                </Box>

                {/* Second Color Picker Box */}
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ mb: 1, display: 'flex', flexDirection: 'column' }}>
                    <span style={{ fontSize: '0.9rem', fontWeight: 600, color: '#334155' }}>Secondary Color</span>
                    <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                      Used for accents, alerts, and borders.
                    </span>
                  </Box>
                  <ColorPicker
                    value={secondaryColor}
                    setValue={setSecondaryColor}
                    disabled={!isGeneralWriteAllowed}
                    sx={{ width: '100%' }}
                  />
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </ContainerStyle>
    </div>
  );
}

function SBLabel({ label, tooltipText, required }: { label: string; tooltipText?: string; required?: boolean }) {
  return (
    <Grid container alignItems={'center'} className="title">
      <FormLabel required={required} className="font-medium md-text mr-1">
        {label}
      </FormLabel>
      {tooltipText && (
        <SbTooltip placement="right" title={tooltipText}>
          <InfoRounded />
        </SbTooltip>
      )}
    </Grid>
  );
}

export default OrganizationSetting;
